import { Component, inject, input, OnInit, signal, viewChild } from "@angular/core";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../core/utils";
import {
  DataTableComponent,
  FormDropdownComponent,
  FormInputFileComponent,
  WfDifferenceComponent,
  WfOverrideDecisionComponent,
} from "../../shared/components";
import { Constants } from "../../shared/constants/constants";
import { AdaaBoolean, FileInputType, Language } from "../../shared/constants/enums";
import { SmartToolTipsDirective, SmartToolTipsService } from "../../shared/directives";
import {
  AttachFile,
  ValueText,
  WorkflowDecisionModelType,
  WorkflowItemDiffModelType,
  WorkflowProcessCtl,
} from "../../shared/models";
import { NotificationCommunication, SearchFields } from "../../shared/models/notification-center-model";
import { SystemLayoutService, WorkflowsApiService } from "../../shared/services";
import { NotificationCenterApiService } from "../../shared/services/notification-center-service";
import {
  buildNotificationDecision,
  buildNotificationSearchFields,
  canReviewNotification,
  notificationReviewInput,
} from "./notification-center.utils";

@Component({
  selector: "adaa-notification-center",
  imports: [
    TranslatePipe,
    FormDropdownComponent,
    DataTableComponent,
    FormInputFileComponent,
    WfDifferenceComponent,
    WfOverrideDecisionComponent,
    SmartToolTipsDirective,
  ],
  templateUrl: "./notification-center.html",
  styleUrl: "./notification-center.scss",
})
export class NotificationCenterComponent implements OnInit {
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _notificationCenterApiService = inject(NotificationCenterApiService);
  private _workflowsApiService = inject(WorkflowsApiService);
  private _smartToolTipsService = inject(SmartToolTipsService);
  systemLayoutService = inject(SystemLayoutService);

  moment = moment;
  AdaaHelper = AdaaHelper;
  AdaaBoolean = AdaaBoolean;
  Language = Language;
  Constants = Constants;

  id = input<string | undefined>();

  actionOptions = signal<ValueText[]>([]);
  readOptions = signal<ValueText[]>([]);
  attachment = signal<AttachFile | null>(null);
  decision = signal<Partial<WorkflowDecisionModelType | undefined>>(undefined);
  notifications = signal<NotificationCommunication[]>([]);
  selectedNotification = signal<NotificationCommunication | undefined>(undefined);
  tableData = signal<WorkflowProcessCtl[]>([]);
  review = signal<WorkflowItemDiffModelType | undefined>(undefined);
  workflowDifferenceRef = viewChild.required<WfDifferenceComponent>("workflowDifference");
  filterTypes = signal<{ action?: string; read?: string }>({
    action: "A",
    read: "N",
  });
  searchFields = signal<SearchFields | undefined>(undefined);
  allowedFiles: FileInputType[] = [
    FileInputType.txt,
    FileInputType.pdf,
    FileInputType.doc,
    FileInputType.docx,
    FileInputType.xls,
    FileInputType.xlsx,
    FileInputType.png,
    FileInputType.jpeg,
    FileInputType.jpg,
  ];

  public ngOnInit(): void {
    this._notificationCenterApiService.getNotifications(true).subscribe({
      next: (response) => this.notifications.set(response.responseData),
      complete: () => {
        if (this.id()) this.getNotification(+this.id()!);
      },
    });

    this._translateService.get("communication.action").subscribe({
      complete: () => {
        this.actionOptions.set([
          { value: "A", text: this._translateService.instant("communication.action") },
          { value: "I", text: this._translateService.instant("communication.info") },
          { value: "", text: this._translateService.instant("communication.all") },
        ]);
        this.readOptions.set([
          { value: AdaaBoolean.Y, text: this._translateService.instant("communication.read") },
          { value: AdaaBoolean.N, text: this._translateService.instant("communication.unread") },
          { value: "", text: this._translateService.instant("communication.read_and_unread") },
        ]);
      },
    });

    this._smartToolTipsService.initSmartToolTips(Constants.SMART_TOOL_TIPS_KEY.SMART_COMMUNICATION_CENTER);
  }

  public performSearch(event: Event) {
    const input = (event.target as HTMLInputElement).value;
    if (input.includes("\\")) return;
    const searchFields = buildNotificationSearchFields(input);
    this.searchFields.set(searchFields);
    this._notificationCenterApiService
      .getNotifications(false, false, { searchFields }, this.filterTypes().action, this.filterTypes().read)
      .subscribe({
        next: (response) => this.notifications.set(response.responseData),
      });
  }

  public getNotification(id: number) {
    const notification = this.notifications().find((notification) => notification.id === id);
    this.selectedNotification.set(notification);
    if (notification?.isRead === AdaaBoolean.N) {
      this._notificationCenterApiService.markAsRead(id).subscribe({
        complete: () => {
          notification.isRead = AdaaBoolean.Y;
          this.notifications.set(this.notifications().map((n) => (n.id === notification.id ? notification : n)));
        },
      });
    }
    if (notification?.isActionPending === AdaaBoolean.Y) {
      this._workflowsApiService.getPreviousWfProcessCtlSteps(notification.wfProcessCtlId).subscribe({
        next: (response) => this.tableData.set(response.responseData),
      });
    }
  }

  public getItemForReview() {
    const selectedNotification = this.selectedNotification();
    if (!selectedNotification || !canReviewNotification(selectedNotification)) return;

    this.workflowDifferenceRef()?.open(notificationReviewInput(selectedNotification));
  }

  public showMoreMessages() {
    this._notificationCenterApiService
      .getNotifications(
        false,
        false,
        this.searchFields() ? { searchFields: this.searchFields()! } : null,
        this.filterTypes().action,
        this.filterTypes().read,
        undefined,
        true
      )
      .subscribe({
        next: (response) => {
          if (response.responseData.length > 0) this.notifications.set(response.responseData);
        },
      });
  }

  public filter(type: string, event: string) {
    if (type === "action") {
      this.filterTypes.set({ ...this.filterTypes(), action: event });
    }
    if (type === "read") {
      this.filterTypes.set({ ...this.filterTypes(), read: event });
    }
    this._notificationCenterApiService
      .getNotifications(
        false,
        false,
        this.searchFields() ? { searchFields: this.searchFields()! } : null,
        this.filterTypes().action,
        this.filterTypes().read
      )
      .subscribe({
        next: (response) => this.notifications.set(response.responseData),
      });
  }

  public cancelDecision() {
    this.selectedNotification.set(undefined);
    this.attachment.set(null);
    this.decision.set(undefined);
  }

  public submitDecision(approval: boolean) {
    const notification = this.selectedNotification();
    if (!notification) return;

    const decision = buildNotificationDecision({
      notification,
      approval,
      attachment: this.attachment(),
      decision: this.decision(),
    });
    this._workflowsApiService.submitWorkflowDecision(decision).subscribe({
      complete: () => {
        this._notificationCenterApiService.getNotifications(true).subscribe({
          next: (response) => {
            this.selectedNotification.set(undefined);
            this.attachment.set(null);
            this.decision.set(undefined);
            this._toastrService.success(this._translateService.instant("notification.success.save"));
            this.notifications.set(response.responseData);
          },
        });
      },
    });
  }

  public order(sort: "asc" | "desc") {
    this._notificationCenterApiService
      .getNotifications(
        false,
        true,
        this.searchFields() ? { searchFields: this.searchFields()! } : null,
        this.filterTypes().action,
        this.filterTypes().read,
        sort
      )
      .subscribe({
        next: (response) => this.notifications.set(response.responseData),
      });
  }
}
